/**
 * Online ordering: the customer app's takeaway and delivery side of `/v1/store` (03-modules.md §8,
 * 05-api.md "Customer API", 06-flows.md F4; M5 task 6). A customer picks an outlet, browses its
 * menu, signs in with a WhatsApp code (SMS fallback), keeps addresses and carts, gets a delivery
 * quote, places an order (items only: the server prices, CLAUDE.md rule 1), pays online, follows
 * it live (with the rider once a courier has it), and later finds it in their history to reorder.
 *
 * Credentials: the public routes (outlet directory, profile, menu, the sign-in code) need none; every
 * route under `/v1/store/me` and the ordering routes take a customer token (`Authorization: Bearer`,
 * an Ed25519 JWT with audience `qrkey-store`), which the sign-in answers. The table visit's diner
 * token (storefront.ts) is a different credential and works on none of these.
 */
import { z } from 'zod';
import { businessDateSchema, isoTimestampSchema, paginated, pageQuerySchema } from './common.js';
import { customerAddressSchema, customerNameSchema, customerPhoneSchema } from './customers.js';
import {
  dropAddressSchema,
  deliveryStatusValueSchema,
  serviceabilityReasonSchema,
  slotAvailabilitySchema,
} from './delivery.js';
import { defineEvent } from './events.js';
import { otpCodeSchema } from './identity.js';
import { uuidSchema } from './ids.js';
import { nonNegativePaiseSchema } from './money.js';
import { ORDER_STATUSES, ORDER_TYPES, FULFILMENT_STATUSES } from './orders.js';
import { geoPointSchema, PRICE_CONTEXTS, THEME_IDS } from './org.js';
import { storeLineInputSchema, storeOrderSchema, MAX_STORE_LINES_PER_ORDER } from './storefront.js';

/** What the customer app can order for later or in a moment from an outlet. */
export const ONLINE_ORDER_TYPES = ['takeaway', 'delivery'] as const;
export type OnlineOrderType = (typeof ONLINE_ORDER_TYPES)[number];
export const onlineOrderTypeSchema = z.enum(ONLINE_ORDER_TYPES);

/** The menu contexts the online menu serves (the order type's own prices). */
export const onlineMenuContextSchema = z.enum(['takeaway', 'delivery']);

/** At most this many saved carts per customer. */
export const MAX_SAVED_CARTS = 10;

// ---------------------------------------------------------------------------
// The outlet directory (public)

export const onlineOutletSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  /** The address printed on the outlet's bills, as one line; null when never set. */
  address: z.string().nullable(),
  location: geoPointSchema.nullable(),
  /** Metres from the point asked about (straight line); null without a point or a pin. */
  distanceM: z.int().nullable(),
});
export type OnlineOutletView = z.output<typeof onlineOutletSchema>;

export const nearbyOutletsQuerySchema = z.object({
  lat: z.preprocess(
    (v) => (typeof v === 'string' && v.trim() !== '' ? Number(v) : v),
    z.number().min(-90).max(90).optional(),
  ),
  lng: z.preprocess(
    (v) => (typeof v === 'string' && v.trim() !== '' ? Number(v) : v),
    z.number().min(-180).max(180).optional(),
  ),
  limit: pageQuerySchema.shape.limit,
});

export const nearbyOutletsSchema = z.object({ data: z.array(onlineOutletSchema) });

/** `GET /v1/store/outlets/{o}`: what the app shows before and while ordering. */
export const onlineOutletProfileSchema = onlineOutletSchema.omit({ distanceM: true }).extend({
  theme: z.enum(THEME_IDS),
  /** The outlet is taking orders (active). */
  open: z.boolean(),
  takeaway: z.object({
    enabled: z.boolean(),
    /** `order.require_acceptance.online`: the order waits for the outlet to accept it. */
    requiresAcceptance: z.boolean(),
  }),
  delivery: z.object({
    /** The outlet has delivery areas set up. */
    enabled: z.boolean(),
    /** The shortest lead time a booked slot needs, in minutes. */
    slotLeadMinutes: z.int(),
  }),
  /** `qr.pay_online`: paying online is offered (the same switch as at the table). */
  payOnline: z.boolean(),
});
export type OnlineOutletProfile = z.output<typeof onlineOutletProfileSchema>;

export const onlineMenuQuerySchema = z.object({
  context: onlineMenuContextSchema.default('takeaway'),
});

// ---------------------------------------------------------------------------
// Sign-in

export const customerOtpRequestSchema = z.object({
  /** The outlet the customer is ordering from: its company is who they sign in to. */
  outletId: uuidSchema,
  phone: customerPhoneSchema,
});

export const customerOtpRequestResultSchema = z.object({
  accepted: z.literal(true),
  /** How the code was sent. */
  channel: z.enum(['whatsapp', 'sms']),
  expiresInSeconds: z.int().positive(),
  resendAfterSeconds: z.int().nonnegative(),
});
export type CustomerOtpRequestResult = z.output<typeof customerOtpRequestResultSchema>;

export const customerOtpVerifySchema = z.object({
  outletId: uuidSchema,
  phone: customerPhoneSchema,
  code: otpCodeSchema,
  /** For a new customer; ignored for a returning one (they edit it on their profile). */
  name: customerNameSchema.optional(),
});

export const customerProfileSchema = z.object({
  id: uuidSchema,
  name: z.string(),
  phone: z.string().nullable(),
  email: z.string().nullable(),
});
export type CustomerProfile = z.output<typeof customerProfileSchema>;

/** `POST /v1/store/otp/verify`: the customer token and who signed in. */
export const customerSessionSchema = z.object({
  /** Send as `Authorization: Bearer …` (and as `{ token }` on /ws). */
  accessToken: z.string().min(1),
  accessTokenExpiresAt: isoTimestampSchema,
  customer: customerProfileSchema,
});
export type CustomerSessionView = z.output<typeof customerSessionSchema>;

export const updateCustomerProfileSchema = z.object({
  name: customerNameSchema.optional(),
  email: z.string().trim().toLowerCase().email().max(200).nullable().optional(),
});

// ---------------------------------------------------------------------------
// Addresses, maps

export const onlineAddressParamsSchema = z.object({ addressId: uuidSchema });

export const onlineAddressListSchema = z.object({ data: z.array(customerAddressSchema) });

export const autocompleteStoreQuerySchema = z.object({
  input: z.string().trim().min(2).max(200),
});
export const storeSuggestionsSchema = z.object({
  data: z.array(z.object({ placeRef: z.string(), description: z.string() })),
});

export const storeGeocodeRequestSchema = z.object({
  address: z.string().trim().min(3).max(400),
  /** A picked suggestion; wins over the typed text. */
  placeRef: z.string().max(200).optional(),
});
export const storeGeocodeSchema = z.object({
  data: z.array(
    z.object({
      formattedAddress: z.string(),
      location: geoPointSchema,
      pincode: z.string().nullable(),
    }),
  ),
});

// ---------------------------------------------------------------------------
// The delivery quote

/** `POST /v1/store/outlets/{o}/delivery-quote`: can we deliver here, what does it cost, and when. */
export const deliveryQuoteRequestSchema = z.object({
  address: dropAddressSchema,
  /** The items total, to check the area's minimum order (the server re-checks at checkout). */
  orderValuePaise: nonNegativePaiseSchema.optional(),
  /** Also return the slots of this date (outlet time); default today. */
  slotDate: businessDateSchema.optional(),
});

export const storeDeliveryQuoteSchema = z.object({
  serviceable: z.boolean(),
  reason: serviceabilityReasonSchema.nullable(),
  /** Something to show the customer. */
  message: z.string().nullable(),
  /** Where the address is on the map (so the app can save the position it found). */
  location: geoPointSchema.nullable(),
  zone: z.object({ id: uuidSchema, name: z.string() }).nullable(),
  distanceM: z.int(),
  /** What the customer pays for delivery: the area's charge (never a courier's live quote). */
  chargePaise: nonNegativePaiseSchema,
  minOrderPaise: nonNegativePaiseSchema,
  /** The fastest estimate of the delivery partners that would take it; null when unknown. */
  etaMinutes: z.int().nullable(),
  /** The date's delivery slots; null when the outlet has none (delivery is as soon as possible). */
  slots: slotAvailabilitySchema.nullable(),
});
export type StoreDeliveryQuoteView = z.output<typeof storeDeliveryQuoteSchema>;

// ---------------------------------------------------------------------------
// Placing an order

const contactSchema = z.object({
  name: customerNameSchema.optional(),
  phone: customerPhoneSchema.optional(),
});

/**
 * `POST /v1/store/outlets/{o}/online-orders`: items, the order type and where it goes. Never a
 * price: the server prices from the published menu in the type's context and adds the
 * delivery area's charge (rule 1).
 */
export const onlineLinesSchema = z
  .array(storeLineInputSchema)
  .min(1)
  .max(MAX_STORE_LINES_PER_ORDER)
  .refine(
    (v) => {
      const ids = v.flatMap((l) => (l.id === undefined ? [] : [l.id]));
      return new Set(ids).size === ids.length;
    },
    { message: 'Each line id at most once' },
  );

export const placeOnlineOrderSchema = z
  .object({
    type: onlineOrderTypeSchema,
    lines: onlineLinesSchema,
    /** Takeaway: when it will be collected (a booked time); omit for as soon as possible. */
    pickupAt: isoTimestampSchema.optional(),
    /** Who collects or receives it, when not the signed-in customer. */
    contact: contactSchema.optional(),
    /** Delivery only. */
    delivery: z
      .object({
        address: dropAddressSchema,
        /** The saved address this came from (kept for the history). */
        addressId: uuidSchema.optional(),
        /** A booked slot; omit for as soon as possible. */
        slot: z.object({ slotId: uuidSchema, date: businessDateSchema }).nullable().optional(),
      })
      .optional(),
  })
  .superRefine((v, ctx) => {
    if (v.type === 'delivery' && v.delivery === undefined) {
      ctx.addIssue({
        code: 'custom',
        path: ['delivery'],
        message: 'Add the delivery address',
      });
    }
    if (v.type === 'takeaway' && v.delivery !== undefined) {
      ctx.addIssue({
        code: 'custom',
        path: ['delivery'],
        message: 'A takeaway order has no delivery address',
      });
    }
    if (v.type === 'delivery' && v.pickupAt !== undefined) {
      ctx.addIssue({
        code: 'custom',
        path: ['pickupAt'],
        message: 'Book a delivery slot instead',
      });
    }
  });
export type PlaceOnlineOrderBody = z.output<typeof placeOnlineOrderSchema>;

// ---------------------------------------------------------------------------
// My orders

/** The rider and the drop, as the customer who ordered sees them. */
export const orderTrackingSchema = z.object({
  address: z.string().nullable(),
  landmark: z.string().nullable(),
  /** The delivery charge on the bill. */
  chargePaise: nonNegativePaiseSchema,
  slot: z
    .object({
      date: businessDateSchema,
      startTime: z.string(),
      endTime: z.string(),
    })
    .nullable(),
  /** The courier's status; null until a courier has been asked for (the outlet is preparing it). */
  status: deliveryStatusValueSchema.nullable(),
  rider: z.object({ name: z.string(), phone: z.string().nullable() }).nullable(),
  riderLocation: z.object({ lat: z.number(), lng: z.number(), at: isoTimestampSchema }).nullable(),
  /** Where it is going (for the map); null once the drop details were erased. */
  dropLocation: geoPointSchema.nullable(),
  /** The code the customer tells the rider at the door; null until the courier issues one. */
  dropCode: z.string().nullable(),
  pickedUpAt: isoTimestampSchema.nullable(),
  deliveredAt: isoTimestampSchema.nullable(),
  failureReason: z.string().nullable(),
});
export type OrderTrackingView = z.output<typeof orderTrackingSchema>;

/** An order of the signed-in customer, with where it is and (for delivery) who has it. */
export const myOrderSchema = storeOrderSchema.extend({
  type: z.enum(ORDER_TYPES),
  outlet: z.object({ id: uuidSchema, name: z.string() }),
  /** The booked time (takeaway pickup or delivery slot start); null for as soon as possible. */
  scheduledFor: isoTimestampSchema.nullable(),
  /** Paying online is possible now (billed or open, unpaid, the outlet offers it). */
  canPay: z.boolean(),
  tracking: orderTrackingSchema.nullable(),
});
export type MyOrderView = z.output<typeof myOrderSchema>;

export const myOrderSummarySchema = z.object({
  id: uuidSchema,
  outletId: uuidSchema,
  outletName: z.string(),
  orderNo: z.int(),
  type: z.enum(ORDER_TYPES),
  status: z.enum(ORDER_STATUSES),
  fulfilmentStatus: z.enum(FULFILMENT_STATUSES),
  totalPaise: nonNegativePaiseSchema,
  /** "2 x Chicken 65, Naan" for the list. */
  summary: z.string(),
  itemCount: z.int(),
  createdAt: isoTimestampSchema,
});
export type MyOrderSummary = z.output<typeof myOrderSummarySchema>;

export const myOrdersQuerySchema = pageQuerySchema.extend({
  /** `active`: not yet over; `past`: delivered, closed, cancelled or rejected. Default all. */
  scope: z.enum(['active', 'past']).optional(),
});
export const myOrderListSchema = paginated(myOrderSummarySchema);

export const myOrderParamsSchema = z.object({ orderId: uuidSchema });

/**
 * `GET /v1/store/me/orders/{id}/reorder`: what the order had, as cart lines (ids and choices
 * only). The app checks them against today's menu; the order it places is priced at today's
 * prices (03-modules.md §8 acceptance).
 */
export const reorderSchema = z.object({
  outletId: uuidSchema,
  type: onlineOrderTypeSchema,
  context: z.enum(PRICE_CONTEXTS),
  lines: z.array(
    z.object({
      productId: uuidSchema,
      variantId: uuidSchema.nullable(),
      optionIds: z.array(uuidSchema),
      addonIds: z.array(uuidSchema),
      qtyMilli: z.int(),
      note: z.string().nullable(),
      name: z.string(),
    }),
  ),
});
export type ReorderView = z.output<typeof reorderSchema>;

// ---------------------------------------------------------------------------
// Saved carts

export const savedCartLineSchema = z.object({
  productId: uuidSchema,
  variantId: uuidSchema.nullable(),
  optionIds: z.array(uuidSchema).max(40),
  addonIds: z.array(uuidSchema).max(40),
  qtyMilli: z.int().min(1).max(50_000),
  note: z.string().max(200),
  /** For display when the menu has not loaded; never trusted. */
  name: z.string().max(200),
});

export const savedCartSchema = z.object({
  id: uuidSchema,
  outletId: uuidSchema,
  name: z.string(),
  type: onlineOrderTypeSchema,
  lines: z.array(savedCartLineSchema),
  createdAt: isoTimestampSchema,
  updatedAt: isoTimestampSchema,
  version: z.int(),
});
export type SavedCartView = z.output<typeof savedCartSchema>;

export const saveCartSchema = z.object({
  /** A new id creates; an existing one of yours replaces the cart (same name and lines: no change). */
  id: uuidSchema,
  outletId: uuidSchema,
  name: z.string().trim().min(1).max(60),
  type: onlineOrderTypeSchema,
  lines: z.array(savedCartLineSchema).min(1).max(MAX_STORE_LINES_PER_ORDER),
});

export const savedCartListSchema = z.object({ data: z.array(savedCartSchema) });
export const savedCartParamsSchema = z.object({ cartId: uuidSchema });

// ---------------------------------------------------------------------------
// Events (no personal data: ids only)

export const customerLoginOpenedV1 = defineEvent(
  'customer_login.opened',
  1,
  z.object({ customerId: uuidSchema, loginId: uuidSchema, outletId: uuidSchema.nullable() }),
);

export const customerLoginClosedV1 = defineEvent(
  'customer_login.closed',
  1,
  z.object({
    customerId: uuidSchema,
    loginId: uuidSchema,
    reason: z.enum(['signed_out', 'erased']),
  }),
);

export const savedCartSavedV1 = defineEvent(
  'saved_cart.saved',
  1,
  z.object({
    cartId: uuidSchema,
    customerId: uuidSchema,
    outletId: uuidSchema,
    lineCount: z.int(),
    created: z.boolean(),
  }),
);

export const savedCartDeletedV1 = defineEvent(
  'saved_cart.deleted',
  1,
  z.object({ cartId: uuidSchema, customerId: uuidSchema, outletId: uuidSchema }),
);

export const ONLINE_EVENTS = [
  customerLoginOpenedV1,
  customerLoginClosedV1,
  savedCartSavedV1,
  savedCartDeletedV1,
] as const;
